/* eslint-disable turbo/no-undeclared-env-vars */
import { createBrowserRouter } from 'react-router-dom';
import { Router } from '@remix-run/router';
import { Outlet } from 'react-router-dom';
import { Root } from './Root/main';

import Edit from './Templates/pages/Edit';
import Create from './Templates/pages/Create';
import Duplicate from './Templates/pages/Duplicate';
import { Index as BrandKitManagementIndex } from './BrandKit';
import { Index as CatalogueManagementIndex } from './CatalogueManagement';
import { Index as AdTagManagementIndex } from './AdTag';
import { Index as VideoTagManagementIndex } from './VideoTag';
import { Index as TemplatesManagementIndex } from './Templates/pages/Index';
import { ProtectedRoute } from './components/ProtectedRoute';
import { NoAccess } from './components/NoAccess';
import { IMarketingCloudUser } from '@ads/iam-library';

// Helper function to check permissions across multiple resources
const checkAnyPermission = async (
  user: IMarketingCloudUser | null,
  action: 'canRead' | 'canCreate' | 'canEdit',
  resources: string[],
) => {
  if (!user) return false;

  const results = await Promise.all(
    resources.map((resource) => user[action]('creative-hub', resource)),
  );

  return results.some((result) => result);
};

// Ad-tag permissions
const checkAdTagPermission = async (user: IMarketingCloudUser | null) =>
  user ? await user.canRead('creative-hub', 'ad-tag') : false;

// Catalogues permissions
const checkCataloguesReadPermission = async (user: IMarketingCloudUser | null) =>
  user ? await user.canRead('creative-hub', 'catalogues') : false;

// Templates & Brand Kit: User has access if they have access to ad-tags OR catalogues
const checkTemplatesPermission = async (user: IMarketingCloudUser | null) =>
  await checkAnyPermission(user, 'canRead', ['ad-tag', 'catalogues']);

const checkTemplatesCreatePermission = async (user: IMarketingCloudUser | null) =>
  await checkAnyPermission(user, 'canCreate', ['ad-tag', 'catalogues']);

const checkTemplatesEditPermission = async (user: IMarketingCloudUser | null) =>
  await checkAnyPermission(user, 'canEdit', ['ad-tag', 'catalogues']);

const checkBrandKitPermission = async (user: IMarketingCloudUser | null) =>
  await checkAnyPermission(user, 'canRead', ['ad-tag', 'catalogues']);

// Video-tag permissions
const checkVideoTagPermission = async (user: IMarketingCloudUser | null) =>
  user ? await user.canRead('creative-hub', 'video-tag') : false;

export function createBrowserRouterWrapper(basename = '/creative-hub'): Router {
  return createBrowserRouter(
    [
      {
        path: '/',
        element: <Root />,
        children: [
          {
            path: 'templates',
            element: (
              <ProtectedRoute checkPermission={checkTemplatesPermission}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute checkPermission={checkTemplatesPermission}>
                    <TemplatesManagementIndex />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'wizard',
                element: (
                  <ProtectedRoute checkPermission={checkTemplatesCreatePermission}>
                    <TemplatesManagementIndex />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <ProtectedRoute checkPermission={checkTemplatesEditPermission}>
                    <Edit />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'create',
                element: (
                  <ProtectedRoute checkPermission={checkTemplatesCreatePermission}>
                    <Create />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'duplicate/:id',
                element: (
                  <ProtectedRoute checkPermission={checkTemplatesEditPermission}>
                    <Duplicate />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: 'brand-kits/*',
            element: (
              <ProtectedRoute checkPermission={checkBrandKitPermission}>
                <BrandKitManagementIndex />
              </ProtectedRoute>
            ),
          },
          {
            path: 'catalogues/*',
            element: (
              <ProtectedRoute checkPermission={checkCataloguesReadPermission}>
                <CatalogueManagementIndex />
              </ProtectedRoute>
            ),
          },
          {
            path: 'ad-tags/*',
            element: (
              <ProtectedRoute checkPermission={checkAdTagPermission}>
                <AdTagManagementIndex />
              </ProtectedRoute>
            ),
          },
          {
            path: 'video-tags/*',
            element: (
              <ProtectedRoute checkPermission={checkVideoTagPermission}>
                <VideoTagManagementIndex />
              </ProtectedRoute>
            ),
          },
          {
            path: 'no-access',
            element: <NoAccess />,
          },
        ],
      },
    ],
    { basename },
  );
}
